import { type FC, useState, useEffect, useRef } from 'react'
import styled from '@emotion/styled'
import type { AnimationItem } from 'lottie-web'

import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'
import ButtonText from '@segment/matcha/components/ButtonText'
import useModal from '@segment/matcha/components/Modal/useModal'

import * as CopyText from '@segment/matcha/recipes/CopyText'
import HeroRecipeBase, * as HeroRecipe from '@segment/matcha/recipes/Hero'

import useIsInView from '@segment/utils/useInView'
import useLottie from '@segment/utils/useLottie/useLottie'

import VideoModal from './../VideoModal'

import heroPng2x from './assets/Hero@2x.png'
import heroWebp from './assets/Hero.webp'
import heroWebp2x from './assets/Hero@2x.webp'
import heroBackground from './assets/HeroBackground.png'
import heroBackgroundWebp from './assets/HeroBackground.webp'
import heroVideoCoverImage from './assets/HeroVideoCoverImage.png'

import jsonUrl from './assets/hero-good-data.json.data'

const copy = {
  heading: `Everything is better with good data`,
  description: `Twilio Segment brings together clean, consented, real-time customer data for every team, so you can deliver personalized customer experiences that grow revenue.`,
  button: `Watch now`,
}

const video = {
  cover: heroVideoCoverImage,
  url: `https://youtu.be/NKjHQMrwPnc`,
}

const Hero: FC = () => {
  const modalRef = useRef(null)
  const modalOpenState = useState(false)
  const [opened] = modalOpenState
  const { getControlOpenProps } = useModal(modalRef, modalOpenState)
  const [animationData, setAnimationData] = useState<Promise<Response>>()
  useEffect(() => {
    setAnimationData(fetch(jsonUrl))
  }, [])
  const [animation, containerRef] = useLottie<AnimationItem, HTMLDivElement>({
    options: {
      autoplay: true,
      animationData,
      loop: true,
    },
  })
  const isInView = useIsInView(containerRef)

  useEffect(() => {
    if (isInView) {
      animation?.play()
    }
  }, [animation, isInView])

  return (
    <Wrapper>
      <StyledHero variant="2Col">
        <HeroRecipe.Copy>
          <CopyText.Heading variant="homeH2" tag="h1">
            {copy.heading}
          </CopyText.Heading>
          <CopyText.Description variant="homePExtraLarge" tag="p">
            {copy.description}
          </CopyText.Description>
          <ButtonText size="medium" tag="a" {...getControlOpenProps()}>
            {copy.button}
            <ChevronRightIcon />
          </ButtonText>
        </HeroRecipe.Copy>
        <HeroRecipe.Contents>
          {animationData ? (
            <Animation ref={containerRef} />
          ) : (
            <picture>
              <source type="image/webp" srcSet={`${heroWebp}, ${heroWebp2x} 2x`} />
              <HeroRecipe.Image
                width="1281"
                height="1080"
                src={heroPng2x}
                alt="Illustration: Personalization is better with good data"
                loading="eager"
              />
            </picture>
          )}
        </HeroRecipe.Contents>
      </StyledHero>
      <Background>
        <picture>
          <source type="image/webp" srcSet={`${heroBackgroundWebp}`} />
          <HeroRecipe.Image src={heroBackground} alt="Hero Background Image" loading="eager" fill={true} />
        </picture>
      </Background>
      {opened ? <VideoModal ref={modalRef} url={video.url} cover={video.cover} autoplay={true} /> : null}
    </Wrapper>
  )
}

const Wrapper = styled.header`
  position: relative;
  background: transparent;
  ${({ theme }) => theme.breakpoints.down(`md`)} {
    background-color: ${({ theme }) => theme.palette.twilio.gray[100]};
  }
`

const Background = styled.div`
  background-color: ${({ theme }) => theme.palette.twilio.gray[100]};
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
`

const StyledHero = styled(HeroRecipeBase)`
  ${HeroRecipe.Wrapper} {
    gap: 50px;
    ${({ theme }) => theme.breakpoints.down(`md`)} {
      gap: 0px;
    }

    ${({ theme }) => theme.breakpoints.up(`sm`)} {
      padding-bottom: 20px;
    }

    ${({ theme }) => theme.breakpoints.up(`md`)} {
      padding-bottom: 40px;
    }

    ${({ theme }) => theme.breakpoints.up(`xl`)} {
      padding-bottom: 0;
      padding-top: 0;
    }
  }
`

const Animation = styled.div`
  width: 100%;
`

export default Hero
